import { Checkbox, cn, ColorPicker, Input, Label, Textarea } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { BookingPageFormDialogContentsSectionForm } from "./types";
import { BookingPageImageField } from "./BookingPageImageField";
import { useTranslation } from "react-i18next";
import { rgbToHex } from "@tellescope/utils";

const PRESET_PRIMARY_COLOR_RGB: Array<[number, number, number]> = [
  [59, 130, 246],
  [16, 185, 129],
  [139, 92, 246],
  [245, 158, 11],
  [239, 68, 68],
  [6, 182, 212],
  [132, 204, 22],
  [236, 72, 153],
  [249, 115, 22],
  [99, 102, 241],
];
const PRESET_BACKGROUND_COLOR_RGB: Array<[number, number, number]> = [
  [255, 255, 255],
  [248, 250, 252],
  [241, 245, 249],
  [226, 232, 240],
  [203, 213, 225],
  [148, 163, 184],
  [100, 116, 139],
  [71, 85, 105],
  [51, 65, 85],
  [30, 41, 59],
];

const PRESET_PRIMARY_COLORS = PRESET_PRIMARY_COLOR_RGB.map(([r, g, b]) => rgbToHex(r, g, b));
const PRESET_BACKGROUND_COLORS = PRESET_BACKGROUND_COLOR_RGB.map(([r, g, b]) => rgbToHex(r, g, b));

export interface CustomizationSectionProps {
  form: BookingPageFormDialogContentsSectionForm<
    | "emailFieldBehavior"
    | "topLogo"
    | "intakeTitle"
    | "calendarTitleText"
    | "intakeDescription"
    | "thankYouRedirectURL"
    | "thankYouTitle"
    | "thankYouHeaderImageURL"
    | "thankYouDescription"
    | "thankYouMainImageURL"
    | "primaryColor"
    | "backgroundColor"
    | "fontURL"
    | "fontFace"
    | "appointmentSlotsMaxHeight"
    | "language"
    | "publicShowLanguage"
  >;
}

export const CustomizationSection = ({ form }: CustomizationSectionProps) => {
  const { t } = useTranslation("calendar");

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="collectReason">{t("Reason For Appointment")}</Label>
        <Select {...form.getCustomFieldProps("emailFieldBehavior")}>
          <Select.Trigger className="w-full" id="emailFieldBehavior">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="required">{t("Required")}</Select.Item>
            <Select.Item value="optional">{t("Optional")}</Select.Item>
            <Select.Item value="hidden">{t("Hidden")}</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <BookingPageImageField
        id="topLogo"
        label={t("Top Logo")}
        value={form.getCustomFieldProps("topLogo").value}
        onValueChange={form.getCustomFieldProps("topLogo").onValueChange}
        orientation="column"
        uploadLabel={t("Select logo image")}
        replaceLabel={t("Replace logo image")}
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor="intakeTitle">{t("Intake Screen Title")}</Label>
          <Input {...form.getFieldProps("intakeTitle")} id="intakeTitle" placeholder={t("Custom intake title")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="calendarTitleText">{t("Calendar Title Text")}</Label>
          <Input
            {...form.getFieldProps("calendarTitleText")}
            id="calendarTitleText"
            placeholder={t("Heading above time slots")}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="intakeDescription">{t("Intake Screen Description")}</Label>
        <Textarea
          {...form.getFieldProps("intakeDescription")}
          id="intakeDescription"
          placeholder={t("Custom intake description")}
        />
      </div>
      <Label>{t("Thank You Screen")}</Label>
      <div className="space-y-2">
        <Label htmlFor="thankYouRedirectURL" className="text-xs text-muted-foreground">
          {t("Redirect URL")}
        </Label>
        <Input
          {...form.getFieldProps("thankYouRedirectURL")}
          id="thankYouRedirectURL"
          placeholder={t("https://example.com/thank-you")}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor="thankYouTitle" className="text-xs text-muted-foreground">
            {t("Title")}
          </Label>
          <Input {...form.getFieldProps("thankYouTitle")} id="thankYouTitle" placeholder={t("Thank you title")} />
        </div>
        <BookingPageImageField
          id="thankYouHeaderImageURL"
          label={t("Header Image")}
          value={form.getCustomFieldProps("thankYouHeaderImageURL").value}
          onValueChange={form.getCustomFieldProps("thankYouHeaderImageURL").onValueChange}
          orientation="column"
          labelClassName={cn("text-xs text-muted-foreground")}
          uploadLabel={t("Select header image")}
          replaceLabel={t("Replace header image")}
        />
        <div className="space-y-2">
          <Label htmlFor="thankYouDescription" className="text-xs text-muted-foreground">
            {t("Description")}
          </Label>
          <Textarea
            {...form.getFieldProps("thankYouDescription")}
            id="thankYouDescription"
            placeholder={t("Thank you description")}
          />
        </div>
        <BookingPageImageField
          id="thankYouMainImageURL"
          label={t("Main Image")}
          value={form.getCustomFieldProps("thankYouMainImageURL").value}
          onValueChange={form.getCustomFieldProps("thankYouMainImageURL").onValueChange}
          orientation="column"
          labelClassName={cn("text-xs text-muted-foreground")}
          uploadLabel={t("Select main image")}
          replaceLabel={t("Replace main image")}
        />
      </div>
      <div className="space-y-4">
        <Label htmlFor="primaryColor">{t("Primary Color")}</Label>
        <ColorPicker
          id="primaryColor"
          {...form.getCustomFieldProps("primaryColor")}
          presetColors={PRESET_PRIMARY_COLORS}
          swatchClassName="rounded-sm"
          clearable
        />
      </div>
      <div className="space-y-4">
        <Label htmlFor="backgroundColor">{t("Background Color")}</Label>
        <ColorPicker
          id="backgroundColor"
          {...form.getCustomFieldProps("backgroundColor")}
          presetColors={PRESET_BACKGROUND_COLORS}
          swatchClassName="rounded-sm"
          clearable
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor="fontURL">{t("Font URL")}</Label>
          <Input {...form.getFieldProps("fontURL")} id="fontURL" placeholder={t("https://fonts.googleapis.com/...")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fontFace">{t("Font Face")}</Label>
          <Input {...form.getFieldProps("fontFace")} id="fontFace" placeholder={t("Font family name")} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="appointmentSlotsMaxHeight">{t("Max Height of Time Slots (pixels)")}</Label>
        <Input
          {...form.getFieldProps("appointmentSlotsMaxHeight")}
          id="appointmentSlotsMaxHeight"
          placeholder={t("Layout control")}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor="language">{t("Default Language")}</Label>
          <Select {...form.getCustomFieldProps("language")}>
            <Select.Trigger id="language" className="w-full">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="English">{t("English")}</Select.Item>
              <Select.Item value="Spanish">{t("Spanish")}</Select.Item>
            </Select.Content>
          </Select>
        </div>
        <span className="flex items-center gap-2">
          <Checkbox
            checked={form.values.publicShowLanguage}
            onCheckedChange={(checked) => form.setFieldValue("publicShowLanguage", !!checked)}
            id="publicShowLanguage"
          />
          <Label id="publicShowLanguage">{t("Show Language Selector")}</Label>
        </span>
      </div>
    </section>
  );
};
